/**
 * Zentrale Prüffunktionen für Listen und Seiten, die Kunden- und Auftragsdaten nur Rollen mit dem Recht zeigen
 * (Story 11.41, [RUDOLF 02.11.2026]; Startwert bis zum Rechte-Editor, Epic 28). Listen, Seiten, Export und KI fragen
 * nur diese Funktionen – nie `role === …`.
 *
 * Abbildung auf den Rechte-Katalog (`catalog.ts`): `canViewCustomers` = `customers:read`, `canViewOrders` =
 * `orders:read`, `canOpenOrderDetail` = `orders:read` mit Öffnen des Auftrags (Preise stehen dort). Story 28.5 tauscht
 * nur die Quelle (`LIST_RIGHT_START_VALUES` → aufgelöste Rechte der Rolle), nicht die Aufrufer.
 *
 * Startwerte: admin und user alles; packplatz sieht Kunde und Auftragsnummer als Text, öffnet aber keinen Auftrag;
 * mitarbeiter weder Auftrag noch Kunde. Sperre im Zweifel: unbekannte oder fehlende Rolle ergibt kein Recht.
 * Rein: keine Datenbank, keine Uhr; läuft auch im Browser.
 */
import type { PermissionKey } from './catalog.ts';

/** Rechte, die eine Listenspalte verlangen kann (`ListColumn.requires`). */
export const LIST_COLUMN_RIGHTS = ['customers', 'orders'] as const;
export type ListColumnRight = (typeof LIST_COLUMN_RIGHTS)[number];

/** Katalog-Schlüssel hinter den Prüffunktionen (Verzeichnis für Story 28.5). */
export const LIST_RIGHT_PERMISSIONS: Readonly<Record<ListColumnRight | 'orderDetail', PermissionKey>> = Object.freeze({
  customers: 'customers:read',
  orders: 'orders:read',
  orderDetail: 'orders:read',
});

interface StartRights {
  customers: boolean;
  orders: boolean;
  orderDetail: boolean;
}

/** Startwerte je heutiger Rolle (`app_user.role`), bis die Rechte der Rolle aus dem Katalog kommen. */
const LIST_RIGHT_START_VALUES: Readonly<Record<string, StartRights>> = Object.freeze({
  admin: { customers: true, orders: true, orderDetail: true },
  user: { customers: true, orders: true, orderDetail: true },
  packplatz: { customers: true, orders: true, orderDetail: false },
  mitarbeiter: { customers: false, orders: false, orderDetail: false },
});

function startRights(role: string | null | undefined): StartRights | null {
  return typeof role === 'string' && Object.hasOwn(LIST_RIGHT_START_VALUES, role) ? LIST_RIGHT_START_VALUES[role]! : null;
}

/** Darf die Rolle Kunden sehen (Kundennummer, Kundenname)? */
export function canViewCustomers(role: string | null | undefined): boolean {
  return startRights(role)?.customers ?? false;
}

/** Darf die Rolle Aufträge sehen (Auftragsnummer als Text)? */
export function canViewOrders(role: string | null | undefined): boolean {
  return startRights(role)?.orders ?? false;
}

/** Darf die Rolle den Auftrag öffnen (Link in die Auftragsansicht mit Preisen)? */
export function canOpenOrderDetail(role: string | null | undefined): boolean {
  return startRights(role)?.orderDetail ?? false;
}

/** Hat die Rolle das Recht, das eine Spalte verlangt? */
export function canViewListColumnRight(role: string | null | undefined, right: ListColumnRight): boolean {
  return right === 'customers' ? canViewCustomers(role) : canViewOrders(role);
}
